import {memo, ReactElement, useContext, useEffect, useState} from "react";
import {useChartOfAccounts} from "../../contexts/chartofaccounts/ChartOfAccountsContext";
import {useProperties} from "../../contexts/properties/PropertiesContext";
import {TSnapshotData} from "./types";
import {Button} from "@zendeskgarden/react-buttons";
import {useGetLatestPortfolioCalcSnapshotInfoLazyQuery, useRequestPortfolioCalcSnapshotCreationMutation} from "../../__generated__/generated_types";
import {Inline} from "@zendeskgarden/react-loaders";
import {ViziblyTheme} from "../../styles/zendesk-garden/ViziblyZDGTheme";
import {ThemeProvider} from "@zendeskgarden/react-theming";
import {Tab, TabList, TabPanel, Tabs} from "@zendeskgarden/react-tabs";
import css from "./styles/css.module.scss";
import {formatterDateTimeUS} from "../../utils/formatters";
import {toast} from "react-toastify";
import FeatureRestrictedOverlay from "../../components/feature-restricted-overlay/FeatureRestrictedOverlay";
import {PortfolioFinancialTablePage} from "./PortfolioFinancialTablePage";
import {PortfolioSideBySideComparison} from "./PortfolioSideBySideComparison";
import {showSideBySidePortfolioComparison} from "../../global/feature_checkers";
import {AuthContext} from "../../contexts/AuthContext";


export function PortfolioFinancialPage(): ReactElement {
    const {user} = useContext(AuthContext);

    const [requestSnapshot] = useRequestPortfolioCalcSnapshotCreationMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [fetchLatestSnapshot, {data: latestSnapshotData, loading: latestSnapshotLoading}] = useGetLatestPortfolioCalcSnapshotInfoLazyQuery({
        fetchPolicy: "no-cache"
    });
    const [currentSnapshot, setCurrentSnapshot] = useState<TSnapshotData>();
    const [creatingSnapshot, setCreatingSnapshot] = useState(false);
    const [secondsBeforeCanRequestSnapshot, setSecondsBeforeCanRequestSnapshot] = useState<number>();
    const [selectedTab, setSelectedTab] = useState("portfolio-financials");
    const coa = useChartOfAccounts();
    const properties = useProperties();
    const budgetYear = properties.properties?.[0]?.budgetYear;

    function pollLatestSnapshotOnce(timeout = 1000) {
        if(budgetYear === undefined) {
            return;
        }

        setTimeout(() => {
            fetchLatestSnapshot({
                variables: {
                    budgetYear: budgetYear
                }
            });
        }, timeout);
    }

    function handleRequestSnapshot() {
        if(budgetYear === undefined) {
            return;
        }

        requestSnapshot({
            variables: {
                budgetYear: budgetYear
            }
        }).then(() => {
            pollLatestSnapshotOnce();
        });
        setCreatingSnapshot(true);
    }

    function startCountdown(targetTime: number) { // targetTime in seconds
        const countdownInterval = setInterval(() => {
            const currentTime = new Date().getTime() / 1000;
            const remainingTime = targetTime - currentTime;

            if(remainingTime <= 0) {
                setSecondsBeforeCanRequestSnapshot(undefined);
                clearInterval(countdownInterval);
            }
            else {
                setSecondsBeforeCanRequestSnapshot(Math.floor(remainingTime));
            }
        }, 1000);
    }

    useEffect(() => {
        if(!budgetYear || currentSnapshot || !properties.properties?.length) {
            return;
        }
        pollLatestSnapshotOnce();
    }, [budgetYear, currentSnapshot, properties.properties]);

    useEffect(() => {
        if(!latestSnapshotData || latestSnapshotLoading) {
            return;
        }
        const latestSnapshot = latestSnapshotData.getLatestPortfolioCalcSnapshotInfo;
        if(!latestSnapshot) {
            if(!creatingSnapshot) {
                handleRequestSnapshot();
            }
            else {
                pollLatestSnapshotOnce();
            }
        }
        else {
            if(currentSnapshot?.snapshotId == latestSnapshot.id) {
                if(creatingSnapshot) {
                    pollLatestSnapshotOnce();
                }
            }
            else {
                const loadedSnapshotTime = new Date(latestSnapshot.createdAt);
                setCurrentSnapshot({
                    snapshotId: latestSnapshot.id,
                    snapshotTime: loadedSnapshotTime
                });
                const secondsSinceLoadedSnapshotTime = (Date.now() - loadedSnapshotTime.getTime()) / 1000;
                const secondsBeforeCanRequestSnapshot = 310 - secondsSinceLoadedSnapshotTime;
                if(secondsBeforeCanRequestSnapshot > 0) {
                    if(creatingSnapshot === true) {
                        toast.success("Data updated");
                    }
                    setSecondsBeforeCanRequestSnapshot(Math.floor(secondsBeforeCanRequestSnapshot));
                    startCountdown(loadedSnapshotTime.getTime() / 1000 + secondsBeforeCanRequestSnapshot + secondsSinceLoadedSnapshotTime);
                }
                else {
                    setSecondsBeforeCanRequestSnapshot(undefined);
                }
                setCreatingSnapshot(false);
            }
        }
    }, [latestSnapshotData, latestSnapshotLoading]);

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <FeatureRestrictedOverlay />
            <div className={css.wrapper}>
                <div className={css.pageHeader}>
                    <h2>Portfolio Analysis</h2>

                    <span style={{marginLeft: "auto"}}></span>

                    {currentSnapshot ?
                        <span className={css.snapshotDate}>Data current as of {formatterDateTimeUS(currentSnapshot.snapshotTime)}</span>
                        :
                        ""
                    }

                    <Button onClick={handleRequestSnapshot} disabled={creatingSnapshot || !properties.properties?.length || secondsBeforeCanRequestSnapshot !== undefined}>
                        {creatingSnapshot
                            ? <Inline size={24} aria-label="loading" />
                            : `Refresh ${secondsBeforeCanRequestSnapshot === undefined ? "" : `(${secondsBeforeCanRequestSnapshot}s)`}`
                        }
                    </Button>
                </div>
                <Tabs selectedItem={selectedTab} onChange={setSelectedTab} style={{height: "100%"}}>
                    <TabList style={{height: "39px"}}>
                        <Tab item="portfolio-financials">Portfolio Financials</Tab>
                        {showSideBySidePortfolioComparison(user) && <Tab item="side-by-side-comparison">Property Comparison</Tab>}
                    </TabList>
                    {selectedTab === "portfolio-financials" &&
                        <TabPanel item="portfolio-financials" style={{height: "calc(100% - 59px)"}}>
                            {coa.chartOfAccounts && properties.properties?.length && coa.isReady && currentSnapshot && budgetYear ?
                                <PortfolioFinancialTablePage
                                    snapshotId={currentSnapshot.snapshotId}
                                    chartOfAccountsTree={coa.chartOfAccounts}
                                    properties={properties.properties}
                                    budgetYear={budgetYear}
                                />
                                : <div>No snapshot data available</div>
                            }
                        </TabPanel>
                    }
                    {showSideBySidePortfolioComparison(user) && selectedTab === "side-by-side-comparison" &&
                        <TabPanel item="side-by-side-comparison" style={{height: "calc(100% - 59px)"}}>
                            {coa.chartOfAccounts && properties.properties?.length && coa.isReady && currentSnapshot && budgetYear ?
                                <PortfolioSideBySideComparisonMemo
                                    snapshotId={currentSnapshot.snapshotId}
                                    chartOfAccountsTree={coa.chartOfAccounts}
                                    properties={properties.properties}
                                />
                                : <div>No snapshot data available</div>
                            }
                        </TabPanel>
                    }
                </Tabs>
            </div>
        </ThemeProvider>
    );
}

const PortfolioSideBySideComparisonMemo = memo(PortfolioSideBySideComparison, (prevProps, nextProps) => {
    return prevProps.snapshotId === nextProps.snapshotId &&
        prevProps.chartOfAccountsTree === nextProps.chartOfAccountsTree &&
        prevProps.properties === nextProps.properties;
});
